import { useEffect, useMemo, useState } from "react";
import { Button, Input, Select } from "antd";
import moment from "moment";
import { IoRefreshOutline, IoSearchOutline } from "react-icons/io5";
import Loader from "../../../../reusableUI/loader";
import { getApiLogs } from "../../../../Service/adminLogs";
import { showToast } from "../../../../reusableUI/toaster";

const defaultLineLimits = [100, 200, 500, 1000];
const allCategoryValue = "ALL";

const prettyPrint = (value) => {
  if (value == null || value === "" || value === "undefined") {
    return "—";
  }

  if (typeof value === "string") {
    const trimmedValue = value.trim();

    if (
      (trimmedValue.startsWith("{") && trimmedValue.endsWith("}")) ||
      (trimmedValue.startsWith("[") && trimmedValue.endsWith("]"))
    ) {
      try {
        return JSON.stringify(JSON.parse(trimmedValue), null, 2);
      } catch (error) {
        return value;
      }
    }

    return value;
  }

  return JSON.stringify(value, null, 2);
};

const getMethodClasses = (method) => {
  const normalizedMethod = String(method || "").toUpperCase();

  switch (normalizedMethod) {
    case "POST":
      return "bg-emerald-50 text-emerald-700 border-emerald-200";
    case "PUT":
    case "PATCH":
      return "bg-amber-50 text-amber-700 border-amber-200";
    case "DELETE":
      return "bg-rose-50 text-rose-700 border-rose-200";
    default:
      return "bg-sky-50 text-sky-700 border-sky-200";
  }
};

const getStatusClasses = (statusCode) => {
  if (statusCode >= 500) return "bg-rose-50 text-rose-700 border-rose-200";
  if (statusCode >= 400) return "bg-amber-50 text-amber-700 border-amber-200";
  if (statusCode >= 200) return "bg-emerald-50 text-emerald-700 border-emerald-200";
  return "bg-slate-50 text-slate-700 border-slate-200";
};

const buildCategoryOptions = (logs) => {
  const uniqueCategories = Array.from(
    new Set(
      logs
        .map((entry) => String(entry?.category || "").trim())
        .filter(Boolean)
    )
  ).sort((first, second) => first.localeCompare(second));

  return [
    { value: allCategoryValue, label: "All APIs" },
    ...uniqueCategories.map((category) => ({
      value: category,
      label: category,
    })),
  ];
};

const ApiLogs = () => {
  const [loading, setLoading] = useState(false);
  const [entries, setEntries] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCategory, setSelectedCategory] = useState(allCategoryValue);
  const [lineLimit, setLineLimit] = useState(100);
  const [lineLimitOptions, setLineLimitOptions] = useState(defaultLineLimits);
  const [loadedLineCount, setLoadedLineCount] = useState(0);
  const [totalLineCount, setTotalLineCount] = useState(0);

  const loadLogs = async (selectedLineLimit = lineLimit) => {
    try {
      setLoading(true);
      const result = await getApiLogs(selectedLineLimit);

      if (result?.error === false) {
        setEntries(result?.data?.entries || []);
        setLoadedLineCount(result?.data?.loadedLineCount || 0);
        setTotalLineCount(result?.data?.totalLineCount || 0);
        setLineLimit(result?.data?.lineLimit || selectedLineLimit);
        setLineLimitOptions(
          result?.data?.availableLineLimits?.length
            ? result.data.availableLineLimits
            : defaultLineLimits
        );
      } else {
        setEntries([]);
        showToast(result?.message || "Failed to fetch API logs", "Error");
      }
    } catch (error) {
      console.error("Error fetching API logs", error);
      setEntries([]);
      showToast("Failed to fetch API logs", "Error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadLogs(100);
  }, []);

  const categoryOptions = useMemo(() => buildCategoryOptions(entries), [entries]);

  const filteredEntries = useMemo(() => {
    const normalizedQuery = searchTerm.trim().toLowerCase();

    return entries.filter((entry) => {
      const matchesCategory =
        selectedCategory === allCategoryValue ||
        entry?.category === selectedCategory;
      const matchesSearch =
        !normalizedQuery ||
        String(entry?.searchableText || "").includes(normalizedQuery);

      return matchesCategory && matchesSearch;
    });
  }, [entries, searchTerm, selectedCategory]);

  return (
    <div className="bg-[#F3F6F5]">
      {loading && <Loader loading={loading} />}

      <div className="w-[98%] bg-white rounded-2xl overflow-hidden h-[87vh] shadow-lg mt-2 m-2 p-4">
        <div className="flex flex-col gap-4 h-full">
          <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
            <div>
              <p className="text-[16px] font-bold text-[#202124]">API Logs</p>
              <p className="text-[12px] text-[#6B7280] mt-1">
                Showing {filteredEntries.length} request entries from the last{" "}
                {loadedLineCount} lines of {totalLineCount} total lines.
              </p>
            </div>

            <div className="flex flex-col gap-3 md:flex-row md:items-center">
              <div className="flex items-center gap-2 border border-[#D9E0E6] rounded-xl px-3 py-2 bg-[#F8FAFC] min-w-[280px]">
                <IoSearchOutline size={18} color="#64748B" />
                <Input
                  bordered={false}
                  value={searchTerm}
                  onChange={(event) => setSearchTerm(event.target.value)}
                  placeholder="Search endpoint, method, status, body, headers, category..."
                  className="text-[12px] bg-transparent"
                />
              </div>

              <Select
                value={selectedCategory}
                onChange={setSelectedCategory}
                className="min-w-[160px]"
                options={categoryOptions}
              />

              <Select
                value={lineLimit}
                onChange={(value) => loadLogs(value)}
                className="min-w-[140px]"
                options={lineLimitOptions.map((value) => ({
                  value,
                  label: `Last ${value} lines`,
                }))}
              />

              <Button
                type="primary"
                icon={<IoRefreshOutline />}
                onClick={() => loadLogs(lineLimit)}
                loading={loading}
              >
                Refresh
              </Button>
            </div>
          </div>

          <div className="flex-1 overflow-y-auto pr-1">
            {filteredEntries.length === 0 ? (
              <div className="h-full flex items-center justify-center border border-dashed border-[#D7DFE9] rounded-2xl bg-[#F8FAFC]">
                <p className="text-[13px] text-[#6B7280]">
                  No matching logs found for the current search or line range.
                </p>
              </div>
            ) : (
              <div className="flex flex-col gap-3">
                {filteredEntries.map((entry) => (
                  <details
                    key={entry.id}
                    className="border border-[#E6ECF2] rounded-2xl bg-[#FCFDFE] shadow-sm group"
                  >
                    <summary className="list-none cursor-pointer p-4">
                      <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
                        <div className="flex flex-col gap-2">
                          <div className="flex flex-wrap items-center gap-2">
                            <span
                              className={`text-[11px] font-semibold px-2.5 py-1 rounded-full border ${getMethodClasses(
                                entry.method
                              )}`}
                            >
                              {entry.method}
                            </span>
                            <span
                              className={`text-[11px] font-semibold px-2.5 py-1 rounded-full border ${getStatusClasses(
                                entry.statusCode
                              )}`}
                            >
                              {entry.statusCode || "N/A"}
                            </span>
                            <span className="text-[11px] font-semibold px-2.5 py-1 rounded-full border bg-violet-50 text-violet-700 border-violet-200">
                              {entry.category || "Other"}
                            </span>
                            {entry.isPartial && (
                              <span className="text-[11px] font-semibold px-2.5 py-1 rounded-full border bg-slate-50 text-slate-700 border-slate-200">
                                Partial entry
                              </span>
                            )}
                          </div>

                          <div>
                            <p className="text-[13px] font-semibold text-[#1F2937] break-all">
                              {entry.endpoint || "Unknown endpoint"}
                            </p>
                            <p className="text-[12px] text-[#6B7280] mt-1 break-all">
                              {entry.ipAddress || "Unknown IP"}
                            </p>
                          </div>
                        </div>

                        <div className="text-left lg:text-right">
                          <p className="text-[12px] font-medium text-[#334155]">
                            {entry.timestamp
                              ? moment(entry.timestamp).format("DD MMM YYYY, hh:mm:ss A")
                              : "Unknown time"}
                          </p>
                          <p className="text-[11px] text-[#94A3B8] mt-1 break-all max-w-[420px]">
                            {entry.userAgent || "No user agent"}
                          </p>
                        </div>
                      </div>
                    </summary>

                    <div className="px-4 pb-4">
                      <div className="grid grid-cols-1 xl:grid-cols-2 gap-3">
                        <div className="rounded-xl border border-[#E6ECF2] bg-white p-3">
                          <p className="text-[12px] font-semibold text-[#475569] mb-2">
                            Request Body
                          </p>
                          <pre className="text-[11px] text-[#0F172A] bg-[#F8FAFC] rounded-lg p-3 overflow-x-auto whitespace-pre-wrap break-words">
                            {prettyPrint(entry.requestBody)}
                          </pre>
                        </div>

                        <div className="rounded-xl border border-[#E6ECF2] bg-white p-3">
                          <p className="text-[12px] font-semibold text-[#475569] mb-2">
                            Response Body
                          </p>
                          <pre className="text-[11px] text-[#0F172A] bg-[#F8FAFC] rounded-lg p-3 overflow-x-auto whitespace-pre-wrap break-words">
                            {prettyPrint(entry.responseBody)}
                          </pre>
                        </div>

                        <div className="rounded-xl border border-[#E6ECF2] bg-white p-3">
                          <p className="text-[12px] font-semibold text-[#475569] mb-2">
                            Request Headers
                          </p>
                          <pre className="text-[11px] text-[#0F172A] bg-[#F8FAFC] rounded-lg p-3 overflow-x-auto whitespace-pre-wrap break-words">
                            {prettyPrint(entry.requestHeaders)}
                          </pre>
                        </div>

                        <div className="rounded-xl border border-[#E6ECF2] bg-white p-3">
                          <p className="text-[12px] font-semibold text-[#475569] mb-2">
                            Response Headers
                          </p>
                          <pre className="text-[11px] text-[#0F172A] bg-[#F8FAFC] rounded-lg p-3 overflow-x-auto whitespace-pre-wrap break-words">
                            {prettyPrint(entry.responseHeaders)}
                          </pre>
                        </div>
                      </div>
                    </div>
                  </details>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ApiLogs;
